import { markdownHasImage, splitMarkdown } from '../utils/chat-ops-parse';
import { createRedactor, getMarkdownType } from '../utils/markdown-utils';

import MarkdownDiffer, { type ChangeObject } from '@affine/core/utils/diff';
/**
 * Markdown差异比较结果接口
 * 包含添加、删除、未变更的内容以及详细的变更记录
 */

export interface MarkdownDiffResult {
  /** 新增的内容数组 */
  added: string[];
  /** 删除的内容数组 */
  removed: string[];
  /** 未变更的内容数组 */
  unchanged: string[];
  /** 详细的变更记录，包含类型、内容、行号等信息 */
  changes: Array<{
    /** 变更类型：添加、删除或未变更 */
    type: 'added' | 'removed' | 'unchanged';
    /** 变更的具体内容 */
    value: string;
    /** 对应的行号 */
    lineNumber: number;
    /** 是否为代码块或表格等特殊块 */
    block?: boolean;
  }>;
}

/**
 * 比较两个Markdown文档的差异，生成详细的差异结果
 *
 * 该函数会：
 * 1. 提取并保护代码块和表格等特殊内容
 * 2. 进行行级别的差异比较
 * 3. 处理包含图片的新增内容，将其拆分为独立部分
 * 4. 生成包含行号信息的变更记录
 *
 * @param oldMarkdown - 原始Markdown文档
 * @param newMarkdown - 新的Markdown文档
 * @returns 包含添加、删除、未变更内容和详细变更记录的差异结果
 * @throws {Error} 当输入参数不是字符串类型时抛出错误
 */
export function diffMarkdown(
  oldMarkdown: string,
  newMarkdown: string
): MarkdownDiffResult {
  // 输入验证
  if (typeof oldMarkdown !== 'string' || typeof newMarkdown !== 'string') {
    throw new Error('输入参数必须是字符串类型');
  }

  // 边界条件处理
  if (oldMarkdown === newMarkdown) {
    return {
      added: [],
      removed: [],
      unchanged: oldMarkdown ? normalizeLines(oldMarkdown) : [],
      changes: [],
    };
  }

  try {
    /** 全局重构器实例 */
    const redactor = createRedactor();
    // 提取并替换代码块和表格为slot
    oldMarkdown = redactor.redactMarkdown(oldMarkdown);
    newMarkdown = redactor.redactMarkdown(newMarkdown);
    const differ = new MarkdownDiffer();
    // 使用JSDiff进行行级别的差异比较
    let diffResult = differ.diffLines(oldMarkdown, newMarkdown);

    console.log('[suggestion] diffResult', diffResult);

    // 初始化结果对象
    const result: MarkdownDiffResult = {
      added: [],
      removed: [],
      unchanged: [],
      changes: [],
    };

    // 遍历 diffResult 还原slot内容
    diffResult = redactor.recoverMarkdown(diffResult);

    console.log('>>>>>>>>>>>>>>>>>>> diffResult-2', diffResult);

    // 优化：合并图片处理和差异处理逻辑，减少循环次数
    let lineNumber = 1;

    for (const part of diffResult) {
      if (part.added) {
        // 处理新增内容，包括图片拆分, 行号不做更新处理
        processAddedContent(part, result, lineNumber);
      } else if (part.removed) {
        // 处理删除内容
        lineNumber = processRemovedContent(part, result, lineNumber);
      } else {
        // 处理未变更内容
        lineNumber = processUnchangedContent(part, result, lineNumber);
      }
    }
    // console.log('>>>>>>>>>>>>>>>>>>> processContent', result.changes);

    // 在后面再进行过滤掉unchanged的变更记录，只保留实际的变更
    // result.changes = result.changes.filter(item => item.type !== 'unchanged');
    // console.log('>>>>>>>>>>>>>>>>>>> filter Content', result.changes);

    return result;
  } catch (error) {
    // 错误处理：提供有意义的错误信息
    const errorMessage = error instanceof Error ? error.message : '未知错误';
    throw new Error(`Markdown差异比较失败: ${errorMessage}`);
  }
}

/**
 * 处理新增内容，包括图片内容的拆分
 * @param part - 差异部分
 * @param result - 结果对象
 * @param lineNumber - 当前行号
 * @throws {Error} 当处理过程中发生错误时抛出
 */
export function processAddedContent(
  part: ChangeObject<string>,
  result: MarkdownDiffResult,
  lineNumber: number
) {
  try {

    // 拆分的block不进行拆分
    if (part.block) {
      result.added.push(part.value);
      result.changes.push({
        type: 'added',
        value: part.value,
        lineNumber,
      });
      return;
    }

    // 将 markdown 拆分为文本和图片部分的数组
    const parts = splitMarkdown(part.value);

    // 遍历所有拆分后的部分
    for (const { value } of parts) {
      if (value) {
        result.added.push(value);
        result.changes.push({
          type: 'added',
          value,
          lineNumber,
        });
      }
    }
  } catch (error) {
    // 处理拆分失败的情况，回退到原始值
    result.added.push(part.value);
    result.changes.push({
      type: 'added',
      value: part.value,
      lineNumber,
    });
  }
}

/**
 * 处理删除内容
 * @param part - 差异部分
 * @param result - 结果对象
 * @param startLineNumber - 起始行号
 * @returns 更新后的行号
 * @throws {Error} 当处理过程中发生错误时抛出
 */
export function processRemovedContent(
  part: ChangeObject<string>,
  result: MarkdownDiffResult,
  startLineNumber: number
): number {
  if (!part.value) return startLineNumber;

  try {
    const lines = normalizeLines(part.value);
    let currentLineNumber = startLineNumber;

    for (const line of lines) {
      result.removed.push(line);
      result.changes.push({
        type: 'removed',
        value: line,
        lineNumber: currentLineNumber,
        block: (part as any).block,
      });
      currentLineNumber++;
    }

    return currentLineNumber;
  } catch (error) {
    // 处理失败时，至少保证行号递增
    return startLineNumber + 1;
  }
}

/**
 * 处理未变更内容
 * @param part - 差异部分
 * @param result - 结果对象
 * @param startLineNumber - 起始行号
 * @returns 更新后的行号
 * @throws {Error} 当处理过程中发生错误时抛出
 */
export function processUnchangedContent(
  part: ChangeObject<string>,
  result: MarkdownDiffResult,
  startLineNumber: number
): number {
  if (!part.value) return startLineNumber;

  try {
    const lines = normalizeLines(part.value);
    let currentLineNumber = startLineNumber;

    for (const line of lines) {
      result.unchanged.push(line);
      result.changes.push({
        type: 'unchanged',
        value: line,
        lineNumber: currentLineNumber,
      });
      currentLineNumber++;
    }

    return currentLineNumber;
  } catch (error) {
    // 处理失败时，至少保证行号递增
    return startLineNumber + 1;
  }
}

/**
 * 对 diffResult 进行排序，依次遍历检查相邻元素相似度
 * 当前后两个元素相似度近似时，调整为 remove 在前 add 在后
 * @param diffResult 原始的差异结果数组
 * @returns 排序后的差异结果数组
 */

/**
 * 规范化行分割，移除尾部空行
 * @param value 要处理的字符串
 * @returns 规范化后的行数组
 * @throws {Error} 当输入不是字符串时抛出错误
 */
function normalizeLines(value: string): string[] {
  if (typeof value !== 'string') {
    throw new Error('normalizeLines: 输入必须是字符串类型');
  }

  if (!value) return [];

  const lines = value.split('\n');
  // 移除尾部空行，但保留中间的空行
  return lines.length > 1 && lines[lines.length - 1] === ''
    ? lines.slice(0, -1)
    : lines;
}

/**
 * 生成格式化的差异报告
 * @param diffResult 差异比较结果
 * @returns 格式化的差异报告字符串
 */
export function formatMarkdownDiff(diffResult: MarkdownDiffResult): string {
  let report = '## Markdown Diff Report\n\n';

  if (diffResult.added.length > 0) {
    report += '### Added Lines:\n';
    diffResult.added.forEach((line, index) => {
      report += `+ ${line}\n`;
    });
    report += '\n';
  }

  if (diffResult.removed.length > 0) {
    report += '### Removed Lines:\n';
    diffResult.removed.forEach((line, index) => {
      report += `- ${line}\n`;
    });
    report += '\n';
  }

  report += `### Summary:\n`;
  report += `- Added: ${diffResult.added.length} lines\n`;
  report += `- Removed: ${diffResult.removed.length} lines\n`;
  report += `- Unchanged: ${diffResult.unchanged.length} lines\n`;

  return report;
}

/**
 * 比较两个markdown文件并返回详细的差异信息
 * @param oldMarkdown 原始markdown内容
 * @param newMarkdown 新的markdown内容
 * @param includeFormatting 是否包含格式化的报告
 * @returns 完整的差异信息
 */
export function compareMarkdownFiles(
  oldMarkdown: string,
  newMarkdown: string,
  includeFormatting: boolean = false
) {
  const diffResult = diffMarkdown(oldMarkdown, newMarkdown);

  const result = {
    ...diffResult,
    hasChanges: diffResult.added.length > 0 || diffResult.removed.length > 0,
    totalChanges: diffResult.added.length + diffResult.removed.length,
  };

  if (includeFormatting) {
    return {
      ...result,
      formattedReport: formatMarkdownDiff(diffResult),
    };
  }

  return result;
}

type ModifyOperation = {
  type: 'add' | 'remove' | 'modify';
  oldValue: string;
  value: string;
  startLineNumber: number;
  endLineNumber: number;
};

export function replaceMarkdown(
  markdown: string,
  operations: ModifyOperation
): string {
  const start = operations.startLineNumber;
  const end = operations.endLineNumber;
  const lines = markdown.split('\n');
  const newLines = operations.value.split('\n');
  lines.splice(start - 1, end - start + 1, ...newLines);
  return lines.join('\n');
}

// 插入新内容
export function insertMarkdown(
  markdown: string,
  operations: ModifyOperation
): string {
  // 在指定位置插入新内容
  const lines = markdown.split('\n');
  const newLines = operations.value.split('\n');
  const start = Math.max(operations.startLineNumber, 0);

  // 插入新内容
  lines.splice(start, 0, ...newLines);

  return lines.join('\n');
}

export enum AI_OPS {
  INSERT = 'insert',
  DELETE = 'delete',
  REPLACE = 'replace',
}

export type Suggestion = {
  op: AI_OPS.INSERT | AI_OPS.DELETE | AI_OPS.REPLACE;
  timestamp: number;
  selection_str: string; // 插入时表示插入在 selection_str 末尾
  start_line_number: number;
  end_line_number: number;
  new_str: string;
};

/**
 * 分组接口
 */
interface ChangeGroup {
  type: 'added' | 'removed';
  lines: string[];
  startLineNumber: number;
  endLineNumber: number;
}

export interface ChangeRecord {
  type: 'added' | 'removed' | 'unchanged';
  value: string;
  lineNumber: number;
  block?: boolean;
}

export function groupSuggestion(changes: ChangeRecord[]): Suggestion[] {
  if (!changes?.length) {
    return [];
  }

  // 应用复杂排序算法
  const relevantChanges = sortRelevantChanges(changes);
  // console.log('>>>>>>>>>>>>>>>>>>> sortRelevantChanges', relevantChanges);

  // 过滤出相关变更（非 unchanged）
  const filteredChanges = changes.filter(
    relevantChanges => relevantChanges.type !== 'unchanged'
  );

  if (!filteredChanges.length) {
    return [];
  }

  const groups = groupConsecutiveChanges(filteredChanges);
  // console.log('>>>>>>>>>>>>>>>>>>> groupConsecutiveChanges', groups);

  const suggestions = generateSuggestions(groups);
  // console.log('>>>>>>>>>>>>>>>>>>> generateSuggestions', suggestions);

  return suggestions;
}

function sortRelevantChanges(changes: ChangeRecord[]): ChangeRecord[] {
  const sortedChanges = [...changes].sort(
    (a, b) => a.lineNumber - b.lineNumber
  );

  let bestMatchScore = 0;
  const MATCH_THRESHOLD = 0.7; // 匹配阈值
  const TEXT_SCORE = 0.5; // 文本匹配阈值
  const TYPE_SCORE = 0.2; // 类型匹配阈值
  const DISTANCE_SCORE = 0.3; // 距离匹配阈值

  let result: ChangeRecord[] = [];
  // 安全获取最大行号，避免空数组访问
  const maxLineNumber =
    sortedChanges.length > 0
      ? sortedChanges[sortedChanges.length - 1].lineNumber
      : 0;
  const differ = new MarkdownDiffer();
  let index = 0;
  while (index < sortedChanges.length) {
    const currentChange = sortedChanges[index];

    if (currentChange.type === 'added' && currentChange.value) {
      let matchedIndex = -1;

      // 向后搜索匹配的 removed 元素，直到遇到 unchanged 元素
      function calculateScore(
        currentChange: ChangeRecord,
        candidateChange: ChangeRecord
      ) {
        let matchScore = 0;

        if (!currentChange || !candidateChange) {
          return matchScore;
        }

        // matchLoc 权重 0.4 (降低权重为行距让路)
        const pattern = currentChange.value.substring(0, 32);
        const matchResult = differ.matchLoc(candidateChange.value, pattern);
        if (matchResult !== -1) {
          matchScore += TEXT_SCORE;
        }

        // matchType 权重 0.3 (降低权重为行距让路)
        const addedType = getMarkdownType(currentChange.value);
        const removedType = getMarkdownType(candidateChange.value);
        if (addedType === removedType && addedType !== 'text') {
          matchScore += TYPE_SCORE;
        }

        const distance = Math.abs(
          currentChange.lineNumber - candidateChange.lineNumber
        );
        // 归一化，最大值由之后的行数决定，避免除零错误
        const maxDistance = maxLineNumber - currentChange.lineNumber;
        if (maxDistance > 0) {
          matchScore +=
            DISTANCE_SCORE - (distance * DISTANCE_SCORE) / maxDistance;
        } else {
          // 如果没有后续行，给予最小距离分数
          matchScore += distance === 0 ? DISTANCE_SCORE : 0;
        }

        return matchScore;
      }

      // 修复数组越界问题：检查 index > 0 才访问前一个元素
      if (index > 0) {
        bestMatchScore = calculateScore(
          currentChange,
          sortedChanges[index - 1]
        );
      } else {
        bestMatchScore = 0; // 第一个元素没有前一个元素可比较
      }

      if (bestMatchScore < MATCH_THRESHOLD) {
        let tempChanges = [];
        // 向前搜索一个单位，加强容错性
        for (let j = index + 1; j < sortedChanges.length; j++) {
          const candidateChange = sortedChanges[j];
          const lastedChange = sortedChanges[j - 1];

          if (
            !candidateChange ||
            !lastedChange ||
            candidateChange.type !== 'removed' ||
            candidateChange.lineNumber !== lastedChange.lineNumber + 1
          ) {
            break;
          }

          tempChanges.push(candidateChange);

          // 如果遇到 unchanged 元素，停止搜索

          // 只考虑 removed 类型且未被处理的元素
          if (candidateChange.value) {
            const matchScore = calculateScore(currentChange, candidateChange);

            // 如果当前匹配分数超过阈值且比之前的最佳匹配更好
            if (matchScore >= MATCH_THRESHOLD && matchScore > bestMatchScore) {
              matchedIndex = j;
              bestMatchScore = matchScore;
              // 如果达到完美匹配（1.0），立即停止搜索
              if (matchScore >= 1.0) {
                break;
              }
            }
          }
        }

        if (matchedIndex !== -1) {
          // 把matchedIndex前面的元素全都加进去
          result = result.concat(tempChanges);
          // 确保added 在removed 元素之后
          currentChange.lineNumber = sortedChanges[matchedIndex].lineNumber + 1;
          index = matchedIndex;
        }
      }
    }
    result.push(currentChange);
    index++;
  }

  return result;
}

/**
 * 将连续的相同类型操作分组
 * @param changes - 已过滤和排序的变更记录
 * @returns 分组结果
 */
function groupConsecutiveChanges(changes: ChangeRecord[]): ChangeGroup[] {
  return changes.reduce((groups, change) => {
    const lastGroup = groups.at(-1);
    if (shouldStartNewGroup(lastGroup, change)) {
      groups.push(createNewGroup(change));
    } else if (lastGroup) {
      addToCurrentGroup(lastGroup, change);
    }
    return groups;
  }, [] as ChangeGroup[]);
}

/**
 * 判断是否应该开始新分组
 * @param currentGroup - 当前分组
 * @param change - 当前变更记录
 * @returns 是否应该开始新分组
 */
function shouldStartNewGroup(
  currentGroup: ChangeGroup | undefined,
  change: ChangeRecord
): boolean {
  return (
    !currentGroup ||
    change.type !== currentGroup.type ||
    change.lineNumber !== currentGroup.endLineNumber + 1 ||
    (change.type === 'added' && markdownHasImage(change.value))
  );
}

/**
 * 创建新分组
 * @param change - 变更记录
 * @returns 新分组
 */
function createNewGroup(change: ChangeRecord): ChangeGroup {
  return {
    type: change.type as 'added' | 'removed',
    lines: [change.value],
    startLineNumber: change.lineNumber,
    endLineNumber: change.lineNumber,
  };
}

/**
 * 将变更添加到当前分组
 * @param group - 当前分组
 * @param change - 变更记录
 */
function addToCurrentGroup(group: ChangeGroup, change: ChangeRecord): void {
  group.lines.push(change.value);
  group.endLineNumber = change.lineNumber;
}

/**
 * 从分组生成建议操作
 * @param groups - 分组数组
 * @returns 建议操作数组
 */
function generateSuggestions(groups: ChangeGroup[]): Suggestion[] {
  const suggestions: Suggestion[] = [];

  let i = 0;
  while (i < groups.length) {
    const currentGroup = groups[i];
    const nextGroup = groups[i + 1];

    if (isReplaceOperation(currentGroup, nextGroup)) {
      suggestions.push({
        op: AI_OPS.REPLACE,
        timestamp: Date.now(),
        selection_str: currentGroup.lines.join('\n'),
        start_line_number: currentGroup.startLineNumber,
        end_line_number: currentGroup.endLineNumber,
        new_str: nextGroup.lines.join('\n'),
      });
      i += 2;
    } else if (currentGroup.type === 'removed') {
      suggestions.push({
        op: AI_OPS.DELETE,
        timestamp: Date.now(),
        selection_str: currentGroup.lines.join('\n'),
        start_line_number: currentGroup.startLineNumber,
        end_line_number: currentGroup.endLineNumber,
        new_str: '',
      });
      i += 1;
    } else if (currentGroup.type === 'added') {
      suggestions.push({
        op: AI_OPS.INSERT,
        timestamp: Date.now(),
        selection_str: '',
        start_line_number: currentGroup.startLineNumber - 1,
        end_line_number: currentGroup.startLineNumber - 1,
        new_str: currentGroup.lines.join('\n'),
      });
      i += 1;
    } else {
      // This case should ideally not be reached if groups are well-formed
      i += 1;
    }
  }
  return suggestions;
}

/**
 * 判断是否为替换操作
 * @param currentGroup - 当前分组
 * @param nextGroup - 下一个分组
 * @returns 是否为替换操作
 */
function isReplaceOperation(
  currentGroup: ChangeGroup,
  nextGroup?: ChangeGroup
): nextGroup is ChangeGroup {
  if (
    !nextGroup ||
    currentGroup.type !== 'removed' ||
    nextGroup.type !== 'added'
  ) {
    return false;
  }

  // 检查是否相邻
  return (
    nextGroup.startLineNumber === currentGroup.startLineNumber ||
    nextGroup.startLineNumber === currentGroup.startLineNumber + 1
  );
}

window.diffMarkdown = diffMarkdown;
